import { FormEvent, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { AuthPageShell } from "@/components/layout/AuthPageShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completePasswordReset, user } = useAuth();

  const token = searchParams.get("token");
  const urlError = searchParams.get("error");
  const isValidToken = useMemo(() => Boolean(token) && !urlError, [token, urlError]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (user && success) return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorMessage(null);

    if (!token) {
      setErrorMessage("This password reset link is invalid or has expired.");
      return;
    }

    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    try {
      await completePasswordReset(password, token);
      setSuccess(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to reset password";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPageShell showBeta>
      <GlassCard className="border-indigo-300/25 bg-slate-950/45 p-7 shadow-[0_25px_80px_-45px_rgba(79,70,229,0.75)]">
        {!isValidToken ? (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-white">Invalid link</h1>
            <p className="mt-2 text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
              This password reset link is invalid or has expired.
            </p>
            <Link
              to="/forgot-password"
              className="mt-4 inline-block text-sm font-medium text-violet-300 transition-colors hover:text-violet-200"
            >
              Request a new reset link
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <h1 className="text-xl font-semibold text-white">
                {success ? "Password updated" : "Set new password"}
              </h1>
              <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-300/85">
                {success
                  ? "Your password has been successfully reset"
                  : "Enter a new password for your account"}
              </p>
            </div>

            {success ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-green-500/30 bg-green-500/15 px-4 py-3 text-sm text-green-300">
                  Your password has been reset successfully. You can now sign in with your new
                  password.
                </div>
                <Button type="button" className="w-full" onClick={() => navigate("/login")}>
                  Continue to login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-200" htmlFor="password">
                    New password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-200" htmlFor="confirmPassword">
                    Confirm password
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Confirm your password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                {errorMessage ? (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/15 px-3 py-2 text-sm text-red-300">
                    {errorMessage}
                  </p>
                ) : null}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Reset password"}
                </Button>
              </form>
            )}

            <p className="mt-4 text-center text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
              <Link
                to="/login"
                className="font-medium text-violet-300 transition-colors hover:text-violet-200"
              >
                Back to login
              </Link>
            </p>
          </>
        )}
      </GlassCard>
    </AuthPageShell>
  );
}
